import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { DatabaseBackup, ShieldAlert, ShieldCheck } from 'lucide-react';
import { MODULES, type OverviewResponse } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Badge, Button, ErrorNotice, PageHeader, Panel, Spinner, formatDateTime, formatRelative, useToast } from '../../ui';
import { actionLabel } from '../audit/actions';

export function OverviewPage() {
  const { me, can } = useSession();
  const q = useQuery({ queryKey: ['overview'], queryFn: () => api.get<OverviewResponse>('/api/overview'), refetchInterval: 60_000 });

  return (
    <>
      <PageHeader title="Security and activity" description="Who can sign in, who is signed in now, and the latest recorded changes." />
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (
        <div className="space-y-6">
          <div className="grid gap-px overflow-hidden rounded-[var(--radius-panel)] border border-line bg-line grid-cols-2 lg:grid-cols-4">
            <Stat label="Active staff" value={q.data.counts.activeUsers} note={q.data.counts.inactiveUsers ? `${q.data.counts.inactiveUsers} deactivated` : 'None deactivated'} to={can('users.view') ? '/staff' : undefined} />
            <Stat label="Signed in now" value={q.data.activeSessions} note="Open sessions across all devices" />
            <Stat
              label="Failed sign-ins today"
              value={q.data.failedLoginsToday}
              note={q.data.counts.lockedUsers ? `${q.data.counts.lockedUsers} account(s) locked` : 'No accounts locked'}
              tone={q.data.counts.lockedUsers ? 'warn' : undefined}
            />
            <Stat label="Branches" value={q.data.counts.branches} note={`${q.data.counts.roles} roles defined`} to={can('branches.manage') ? '/branches' : undefined} />
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <Panel title="Recent activity" actions={can('audit.view') ? <Link to="/audit" className="text-sm font-medium text-ledger-600 hover:underline">Open audit log</Link> : undefined}>
              <ol className="divide-y divide-line">
                {q.data.recentActivity.map((e) => (
                  <li key={e.seq} className="flex gap-4 px-5 py-3">
                    <span className="w-24 shrink-0 text-sm text-muted num">{formatRelative(e.createdAt)}</span>
                    <div className="min-w-0">
                      <p className="text-[15px] leading-snug">{e.summary}</p>
                      <p className="text-sm text-muted">
                        {actionLabel(e.action)} by {e.userName ?? 'System'}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </Panel>

            <div className="space-y-6">
              {can('settings.manage') && <BackupsPanel />}
              <Panel title="Audit trail integrity">
                <div className="flex items-start gap-3 p-5">
                  {q.data.auditIntegrity.ok ? (
                    <ShieldCheck className="mt-0.5 size-6 shrink-0 text-ok" aria-hidden />
                  ) : (
                    <ShieldAlert className="mt-0.5 size-6 shrink-0 text-danger" aria-hidden />
                  )}
                  <div>
                    <p className="font-medium">{q.data.auditIntegrity.ok ? 'Intact' : 'Tampering detected'}</p>
                    <p className="text-sm text-muted">
                      {q.data.auditIntegrity.ok
                        ? `All ${q.data.auditIntegrity.checked.toLocaleString()} entries match their fingerprints. No record has been edited or removed.`
                        : `Entry #${q.data.auditIntegrity.firstBrokenSeq} no longer matches its fingerprint. Someone changed the database directly. Restore from backup and investigate.`}
                    </p>
                  </div>
                </div>
              </Panel>

              <Panel title="Features for your business">
                <ul className="divide-y divide-line">
                  {me.business.enabledModules.map((m) => (
                    <li key={m} className="flex items-center justify-between gap-3 px-5 py-2.5">
                      <span>{MODULES[m].label}</span>
                      <Badge tone="ok">On</Badge>
                    </li>
                  ))}
                </ul>
                <p className="border-t border-line px-5 py-3 text-sm text-muted">
                  {can('settings.manage') && (
                    <Link to="/settings" className="font-medium text-ledger-600 hover:underline">
                      Change features
                    </Link>
                  )}
                </p>
              </Panel>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, note, to, tone }: { label: string; value: number; note: string; to?: string; tone?: 'warn' }) {
  const body = (
    <>
      <p className="text-sm text-muted">{label}</p>
      <p className={`mt-1 text-[28px] sm:text-[34px] font-semibold leading-none num ${tone === 'warn' ? 'text-warn' : ''}`}>{value.toLocaleString()}</p>
      <p className="mt-2 text-sm text-muted">{note}</p>
    </>
  );
  return to ? (
    <Link to={to} className="block bg-surface p-4 sm:p-5 hover:bg-ledger-50">
      {body}
    </Link>
  ) : (
    <div className="bg-surface p-4 sm:p-5">{body}</div>
  );
}

interface BackupInfo {
  file: string;
  createdAt: string;
  reason: 'scheduled' | 'manual';
  sizeBytes: number;
  verified: boolean;
  verification: string;
}

/** Latest backups and a "back up now" button. The files stay on the server. */
function BackupsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['backups'], queryFn: () => api.get<{ dir: string; keep: number; hour: number | null; backups: BackupInfo[] }>('/api/backups') });
  const now = useMutation({
    mutationFn: () => api.post<BackupInfo>('/api/backups'),
    onSuccess: (b) => {
      toast(b.verified ? 'Backup written and checked' : 'Backup written but its check failed', b.verified ? 'ok' : 'danger');
      void qc.invalidateQueries({ queryKey: ['backups'] });
    },
  });
  const latest = q.data?.backups[0];
  const ageH = latest ? (Date.now() - Date.parse(latest.createdAt)) / 3_600_000 : Infinity;
  return (
    <Panel title="Backups" actions={<Button size="sm" icon={<DatabaseBackup className="size-3.5" />} busy={now.isPending} onClick={() => now.mutate()}>Back up now</Button>}>
      <div className="p-5">
        {q.isLoading && <Spinner />}
        {now.error && <ErrorNotice error={now.error} />}
        {q.data && !latest && <p className="font-medium text-warn">No backup yet. Take one now.</p>}
        {latest && (
          <>
            <p className={ageH > 30 ? 'font-medium text-warn' : 'font-medium'}>
              Last backup {formatRelative(latest.createdAt)} {latest.verified ? '— checked' : '— CHECK FAILED'}
            </p>
            <p className="text-sm text-muted">{latest.verification}</p>
          </>
        )}
        {q.data && (
          <p className="mt-2 text-sm text-muted">
            {q.data.hour == null ? 'Automatic backups are off.' : `Taken automatically each night after ${String(q.data.hour).padStart(2, '0')}:00; the newest ${q.data.keep} are kept`} in{' '}
            <span className="break-all font-mono text-xs">{q.data.dir}</span>. Copy this folder to another place (USB drive or cloud folder) every week.
          </p>
        )}
        {!!q.data?.backups.length && (
          <ul className="mt-3 divide-y divide-line text-sm">
            {q.data.backups.slice(0, 5).map((b) => (
              <li key={b.file} className="flex justify-between gap-3 py-1.5">
                <span>{formatDateTime(b.createdAt)} <span className="text-muted">({b.reason === 'manual' ? 'by hand' : 'nightly'})</span></span>
                <span className="text-muted num">{(b.sizeBytes / 1_048_576).toFixed(1)} MB {b.verified ? '' : '· failed check'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
